
import { db, serverTimestamp, doc, setDoc, getDoc, collection, query, where, getDocs, limit, functions } from '../../firebaseClient';
import {
  ApprovalModule,
  ApprovalFlow,
  ApprovalStage,
  ApprovalStageKey,
  ApprovalFlowStatus,
  DeptDecision
} from '../types/approval';
import { logActivity } from './activityLog';

// [Synced] Approval Engine v4.6.1

const COLLECTION = 'approvalFlows';

export function buildThreeStageFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  coReviewDepts?: string[]
): ApprovalFlow {
  
  const stages: ApprovalStage[] = [];

  // 1. Optional CO_REVIEW Stage
  if (module === 'CONTRACT_REVIEW' && coReviewDepts && coReviewDepts.length > 0) {
    const initialDecisions: Record<string, DeptDecision> = {};
    coReviewDepts.forEach(deptId => {
      initialDecisions[deptId] = { status: 'PENDING' };
    });

    stages.push({
      key: 'CO_REVIEW',
      label: '多部門會辦',
      approverRole: 'multi-dept',
      status: 'PENDING',
      coReviewDepts: coReviewDepts,
      deptDecisions: initialDecisions
    });
  }

  // 2. Standard Stages
  stages.push(
    {
      key: 'LEGAL_REVIEW',
      label: '法務審查',
      approverRole: 'legal',
      status: (stages.length > 0) ? 'NOT_STARTED' : 'PENDING',
    },
    {
      key: 'MANAGER_LISA',
      label: '主管核決 (Lisa)',
      approverRole: 'lisa',
      status: 'NOT_STARTED',
    },
    {
      key: 'MANAGER_IRENE',
      label: '主管核決 (Irene)',
      approverRole: 'irene',
      status: 'NOT_STARTED',
    }
  );

  const flow: any = {
    id: `${module}_${targetId}`,
    module,
    targetId,
    createdBy,
    createdDept,
    assigneeName,
    createdAt: new Date().toISOString(),
    status: 'IN_PROGRESS',
    currentStageKey: stages[0].key,
    stages,
  };

  return flow as ApprovalFlow;
}

export async function createOrResetApprovalFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  options?: { coReviewDepts?: string[] }
) {
  const flow = buildThreeStageFlow(
    module, 
    targetId, 
    createdBy, 
    createdDept, 
    assigneeName, 
    options?.coReviewDepts
  );
  
  const flowRef = doc(db, COLLECTION, flow.id);
  await setDoc(flowRef, {
    ...flow,
    createdAt: serverTimestamp(),
  }, { merge: true });

  const auditModule = (module === 'SEAL_REQUEST' || module === ('STAMP_REQUEST' as any)) ? 'seal' : module;
  await logActivity({
    module: auditModule as any,
    action: "start_approval_flow",
    targetId: targetId,
    entityId: flow.id,
    entityType: "ApprovalFlow",
    actorId: createdBy,
    actorName: assigneeName || "System",
    details: `Started approval flow for ${module}`
  });

  return flow;
}

export async function getApprovalFlowStrict(params: { flowId?: string, module?: string, targetId?: string }): Promise<ApprovalFlow | null> {
    const { flowId, module, targetId } = params;

    if (flowId) {
        const snap = await getDoc(doc(db, COLLECTION, flowId));
        if (snap.exists) return snap.data() as ApprovalFlow;
    }

    if (module && targetId) {
        const stdId = `${module}_${targetId}`;
        const snapStd = await getDoc(doc(db, COLLECTION, stdId));
        if (snapStd.exists) return snapStd.data() as ApprovalFlow;

        // Legacy check
        let legacyModule = module;
        if (module === 'SEAL_REQUEST') legacyModule = 'sealRequest';
        else if (module === 'CONTRACT_REVIEW') legacyModule = 'contractDraft';
        
        if (legacyModule !== module) {
            const legacyId = `${legacyModule}_${targetId}`;
            const snapLeg = await getDoc(doc(db, COLLECTION, legacyId));
            if (snapLeg.exists) return snapLeg.data() as ApprovalFlow;
        }
    }

    if (targetId) {
        const q = query(collection(db, COLLECTION), where("targetId", "==", targetId), limit(1));
        const qSnap = await getDocs(q);
        if (!qSnap.empty) return qSnap.docs[0].data() as ApprovalFlow;
    }

    return null;
}

/**
 * Executes an approval action via Backend Callable.
 */
export async function updateStageDecision(params: {
  flowId: string;
  stageKey: ApprovalStageKey;
  decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | 'DONE';
  comment: string;
  actorUid: string;
  actorName: string;
  deptCode?: string; 
}) {
  const fn = functions.httpsCallable('submitApprovalAction');
  
  try {
      const res = await fn({
          flowId: params.flowId,
          stageKey: params.stageKey,
          decision: params.decision,
          comment: params.comment,
          deptCode: params.deptCode
      });
      
      const result = res.data as any;
      // Note: HttpsCallable throws error if status != OK, so this block might be redundant but safe
      if (result && result.ok === false) {
          throw new Error(result.error || "Approval action failed on server.");
      }
      return result;
  } catch (e: any) {
      console.error("[updateStageDecision] Failed:", e);
      // Propagate the specific message if available
      // Firebase errors often have details inside the object
      const details = e.details || {};
      if (details.originalMessage) {
          // Re-throw with clean message
          const err = new Error(details.originalMessage);
          (err as any).details = details;
          throw err;
      }
      // Re-throw original to let caller handle
      throw e;
  }
}
